/* ============================================================
   DATAKULT TERMINAL — interactive DOS portfolio for Philippe Küng
   ============================================================ */
// ============================================================
// AUDIO CONFIGURATION — single place to swap asset URLs.
// ============================================================
const AUDIO_CONFIG = {
    // Eric Matyas "Cold Moon" — used with attribution per soundimage.org license.
    // Self-hosted in public/sounds/ to sidestep any cross-origin / hotlink issues.
    ambient: '/sounds/Cold-Moon_Looping.ogg',
    ambientVolume: 0.25,
    // humVolume 0 disables the hum layer — the placeholder file at
    // /sounds/computer-fan-ambient.mp3 is corrupt (2 bytes). Set to a small
    // positive number once a real ship-hum file is dropped in public/sounds/.
    humVolume: 0,
    // Intentionally restrained: closer to a damped mechanical switch than
    // a full-volume typewriter sample.
    sfxVolume: 0.16,
    keySamples: {
        normal: [],
        heavy: [],
    },
};
// ---------- DOM ----------
const monitorBezel = document.getElementById('monitor-bezel') as HTMLDivElement;
const changePageButton = document.getElementById('changePageButton') as HTMLButtonElement;
const terminalContainer = document.getElementById('terminal-container') as HTMLDivElement;
const terminalOutput = document.getElementById('terminal-output') as HTMLPreElement;
const cursorElement = document.getElementById('cursor') as HTMLSpanElement;
const currentInputElement = document.createElement('span'); // Create a dedicated span for user input
const escapeButton = document.getElementById('escapeButton') as HTMLButtonElement;
const humSound = document.getElementById('humSound') as HTMLAudioElement;
const ambientSound = document.getElementById('ambientSound') as HTMLAudioElement | null;
const matrixCanvas = document.getElementById('matrix-rain') as HTMLCanvasElement | null;
const muteSfxBtn = document.getElementById('muteSfxBtn') as HTMLButtonElement | null;
const muteAmbientBtn = document.getElementById('muteAmbientBtn') as HTMLButtonElement | null;
const creditsBtn = document.getElementById('creditsBtn') as HTMLButtonElement | null;
const creditsModal = document.getElementById('credits-modal') as HTMLDivElement | null;
const creditsClose = document.getElementById('creditsClose') as HTMLButtonElement | null;
// A real input is required to summon phone keyboards. It remains visually
// hidden while the terminal span stays the single rendered source of truth.
const mobileCommandInput = document.createElement('input');
mobileCommandInput.id = 'mobile-command-input';
mobileCommandInput.type = 'text';
mobileCommandInput.autocomplete = 'off';
mobileCommandInput.autocapitalize = 'off';
mobileCommandInput.spellcheck = false;
mobileCommandInput.setAttribute('aria-label', 'Terminal command');
document.body.appendChild(mobileCommandInput);
// ---------- Audio ----------
const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
const audioCtx: AudioContext = new AudioCtx();
// Mute state — split into SFX vs ambient, persisted to localStorage.
let sfxMuted = localStorage.getItem('dk-mute-sfx') === '1';
let ambientMuted = localStorage.getItem('dk-mute-ambient') === '1';
// Optional sample pools (HTMLAudioElement) — used only if AUDIO_CONFIG.keySamples is populated.
const keySamplePools: Record<'normal' | 'heavy', HTMLAudioElement[]> = {
    normal: AUDIO_CONFIG.keySamples.normal.map(src => { const a = new Audio(src); a.preload = 'auto'; return a; }),
    heavy: AUDIO_CONFIG.keySamples.heavy.map(src => { const a = new Audio(src); a.preload = 'auto'; return a; }),
};
type KeyFlavor = 'normal' | 'heavy';
// Mechanical key click — procedural so we get free per-stroke variation.
// `heavy` is used for Enter / Backspace / Escape / error feedback.
function playKeyClick(flavor: KeyFlavor = 'normal'): void {
    if (sfxMuted) return;
    if (audioCtx.state === 'suspended') audioCtx.resume();

    const t = audioCtx.currentTime;

    // Prefer real samples when configured; otherwise synthesize.
    const pool = keySamplePools[flavor];
    if (pool.length > 0) {
        try {
            const src = pool[Math.floor(Math.random() * pool.length)];
            const node = src.cloneNode(true) as HTMLAudioElement;
            node.volume = AUDIO_CONFIG.sfxVolume * (0.85 + Math.random() * 0.3) * (flavor === 'heavy' ? 1.1 : 1);
            node.playbackRate = 0.92 + Math.random() * 0.16;
            node.play().catch(() => synthesizeKey(flavor, t));
            return;
        } catch {
            /* fall through to synthesis */
        }
    }
    synthesizeKey(flavor, t);
}
function synthesizeKey(flavor: KeyFlavor, t: number): void {
    const isHeavy = flavor === 'heavy';
    // Per-stroke jitter — keeps repeated typing organic.
    const pitchJ = 0.85 + Math.random() * 0.3;
    const volJ = 0.85 + Math.random() * 0.3;
    const heavyMul = isHeavy ? 1.12 : 1;
    const masterVol = AUDIO_CONFIG.sfxVolume * volJ * heavyMul;

    // 1. Noise burst — chassis hit on the keyswitch.
    const noiseDur = isHeavy ? 0.036 : 0.028;
    const buf = audioCtx.createBuffer(1, Math.floor(audioCtx.sampleRate * noiseDur), audioCtx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < buf.length; i++) data[i] = Math.random() * 2 - 1;
    const noise = audioCtx.createBufferSource();
    noise.buffer = buf;
    const lp = audioCtx.createBiquadFilter();
    lp.type = 'bandpass';
    lp.frequency.value = (isHeavy ? 1250 : 1750) * pitchJ;
    lp.Q.value = 1.35;
    const ng = audioCtx.createGain();
    ng.gain.setValueAtTime(0.055 * masterVol, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + noiseDur);
    noise.connect(lp).connect(ng).connect(audioCtx.destination);
    noise.start(t);

    // 2. Switch click — short transient.
    const osc = audioCtx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime((isHeavy ? 1050 : 1450) * pitchJ, t);
    osc.frequency.exponentialRampToValueAtTime((isHeavy ? 520 : 720) * pitchJ, t + 0.012);
    const cg = audioCtx.createGain();
    cg.gain.setValueAtTime(0.038 * masterVol, t);
    cg.gain.exponentialRampToValueAtTime(0.001, t + 0.018);
    osc.connect(cg).connect(audioCtx.destination);
    osc.start(t);
    osc.stop(t + 0.02);

    // 3. Bottom-out thud — heavier on Enter/Backspace.
    const thud = audioCtx.createOscillator();
    thud.type = 'sine';
    const thudDur = isHeavy ? 0.07 : 0.048;
    thud.frequency.setValueAtTime((isHeavy ? 135 : 175) * pitchJ, t);
    thud.frequency.exponentialRampToValueAtTime((isHeavy ? 70 : 90) * pitchJ, t + thudDur);
    const tg = audioCtx.createGain();
    tg.gain.setValueAtTime((isHeavy ? 0.15 : 0.1) * masterVol, t);
    tg.gain.exponentialRampToValueAtTime(0.001, t + thudDur);
    thud.connect(tg).connect(audioCtx.destination);
    thud.start(t);
    thud.stop(t + thudDur);
}
function playBeep(freq = 880, dur = 0.1, type: OscillatorType = 'square'): void {
    if (sfxMuted) return;
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const t = audioCtx.currentTime;
    const o = audioCtx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = audioCtx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.15, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(audioCtx.destination);
    o.start(t);
    o.stop(t + dur + 0.05);
}
// ============================================================
// AMBIENT AUDIO + MUTE TOGGLES + CREDITS
// ============================================================
let ambientStarted = false;
function applyAmbientState(): void {
    if (humSound) humSound.muted = ambientMuted;
    if (ambientSound) ambientSound.muted = ambientMuted;
}
// Browsers block autoplay until the user interacts. Power-on or panel-click
// counts as that interaction; this kicks in both layers idempotently.
function ensureAmbientStarted(): void {
    if (ambientStarted) return;
    ambientStarted = true;

    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {/* ignore */});

    if (humSound && AUDIO_CONFIG.humVolume > 0) {
        humSound.volume = AUDIO_CONFIG.humVolume;
        humSound.play().catch(err => console.warn('[audio] hum failed to play:', err));
    }
    if (ambientSound && AUDIO_CONFIG.ambient) {
        // Fallback: HTML is the source of truth for src, but if it's empty we set it here.
        if (!ambientSound.getAttribute('src')) ambientSound.src = AUDIO_CONFIG.ambient;
        ambientSound.volume = AUDIO_CONFIG.ambientVolume;
        console.info('[audio] ambient pre-play state:',
            'src=', ambientSound.currentSrc,
            'readyState=', ambientSound.readyState,
            'muted=', ambientMuted,
            'networkState=', ambientSound.networkState);
        ambientSound.addEventListener('error', () => {
            console.warn('[audio] ambient track failed to load:', ambientSound.error);
        }, { once: true });
        ambientSound.play()
            .then(() => console.info('[audio] ambient playback started'))
            .catch(err => console.warn('[audio] ambient play() rejected:', err));
    }
    applyAmbientState();
}
function setSfxMuted(m: boolean): void {
    sfxMuted = m;
    localStorage.setItem('dk-mute-sfx', m ? '1' : '0');
    if (muteSfxBtn) {
        muteSfxBtn.classList.toggle('is-active', !m);
        muteSfxBtn.setAttribute('aria-pressed', String(!m));
    }
}
function setAmbientMuted(m: boolean): void {
    ambientMuted = m;
    localStorage.setItem('dk-mute-ambient', m ? '1' : '0');
    applyAmbientState();
    if (muteAmbientBtn) {
        muteAmbientBtn.classList.toggle('is-active', !m);
        muteAmbientBtn.setAttribute('aria-pressed', String(!m));
    }
}
function openCredits(): void { creditsModal?.classList.remove('hidden'); }
function closeCredits(): void { creditsModal?.classList.add('hidden'); }
// Initialize toggle UI from persisted state.
setSfxMuted(sfxMuted);
setAmbientMuted(ambientMuted);
muteSfxBtn?.addEventListener('click', () => {
    ensureAmbientStarted();
    setSfxMuted(!sfxMuted);
    if (!sfxMuted) playKeyClick('normal'); // audible confirmation when un-muting
});
muteAmbientBtn?.addEventListener('click', () => {
    ensureAmbientStarted();
    setAmbientMuted(!ambientMuted);
});
creditsBtn?.addEventListener('click', openCredits);
creditsClose?.addEventListener('click', closeCredits);
creditsModal?.addEventListener('click', (e) => {
    if (e.target === creditsModal) closeCredits();
});
// ---------- State ----------
let isScreenOn = false;
let isTyping = false;
let acceptingInput = false;
const history: string[] = [];
let historyIdx = -1;
let currentInput = '';
let matrixActive = false;
const PROMPT = 'C:\\>';
// ---------- Utility ----------
const sleep = (ms: number): Promise<void> => new Promise(r => setTimeout(r, ms));
function append(text: string): void {
    terminalOutput.append(document.createTextNode(text));
    terminalOutput.scrollTop = terminalOutput.scrollHeight;
}

interface TypeOpts { speed?: number; silent?: boolean; chance?: number }

async function type(line: string, opts: TypeOpts = {}): Promise<void> {
    const speed = opts.speed ?? 18;
    // chance defaults to 0.75 (most chars play); pass a lower value for boot
    // text so the cascade does not turn into a buzz of clicks.
    const chance = opts.chance ?? 0.75;
    for (const ch of line) {
        append(ch);
        if (!opts.silent && ch !== ' ' && Math.random() < chance) playKeyClick();
        await sleep(Math.random() * 8 + speed * 0.6);
    }
    append('\n');
}

async function typeLines(lines: string[], opts?: TypeOpts): Promise<void> {
    for (const l of lines) await type(l, opts);
}
function instant(lines: string[]): void {
    append(lines.join('\n') + '\n');
}
function showPrompt(): void {
    append(PROMPT);
    acceptingInput = true;
    currentInput = '';
    currentInputElement.textContent = '';
    // Append the input span and the cursor INSIDE terminalOutput so the
    // cursor flows inline immediately after whatever the user has typed.
    // appendChild moves the element if it already lives elsewhere in the DOM.
    terminalOutput.appendChild(currentInputElement);
    if (cursorElement) {
        terminalOutput.appendChild(cursorElement);
        cursorElement.classList.remove('hidden');
    }
    terminalOutput.scrollTop = terminalOutput.scrollHeight;
}
// ---------- Content ----------

const BANNER = [
    '   ____    _  _____  _    _  ___   _ _   _____ ',
    '  |  _ \\  / \\|_   _|/ \\  | |/ / | | | | |_   _|',
    '  | | | |/ _ \\ | | / _ \\ | \' /| | | | |   | |  ',
    '  | |_| / ___ \\| |/ ___ \\| . \\| |_| | |___| |  ',
    '  |____/_/   \\_\\_/_/   \\_\\_|\\_\\\\___/|_____|_|  ',
    '',
    '         T E R M I N A L   v 4 . 7   ( 1 9 8 4 )',
];
const POST = [
    'DATAKULT BIOS v4.7 (C) 1984 Datakult Industries, Lisbon',
    'CPU: NeuroSym 8086 @ 4.77 MHz .................. [ OK ]',
    'Memory Test: 640KB conventional ................ [ OK ]',
    '             64MB extended .................... [ OK ]',
    'Detecting IDE Drives ........................... [ OK ]',
    '  Master: DATAKULT-HD 1.21 GB',
    '  Slave : NEUROLYMIA-CD-ROM',
    'Loading neural co-processor ORCHID ............. [ OK ]',
    'Loading agentic kernel HAC ..................... [ OK ]',
    'Initialising SynSense interface ................ [ OK ]',
    'Press DEL to enter SETUP, or wait for boot ......',
];
const ABOUT = [
    '',
    'NAME      : Philippe Küng (aka Datakult)',
    'ROLE      : Founder, Clinic of AI',
    'BASED     : Lisbon, PT  /  Zurich, CH',
    'PASSPORTS : Swiss + German',
    '',
    'I build AI systems for humans who want to stop',
    'doing the work computers should be doing.',
    '',
    'Polymath at the seam of GenAI, agentic systems,',
    'data engineering and behavioural design. I run',
    'Clinic of AI, an enterprise AI agency turning',
    'broken workflows into deployed automations.',
    '',
    'Before that: Head of Data & AI at Crayon AG,',
    'where I led 10+ Fortune-500 transformation',
    'programmes on the Microsoft AI stack.',
    '',
    "Type 'help' to see what else this terminal can do.",
];
const SKILLS = [
    '',
    'CORE STACK',
    '----------',
    '  GenAI / LLMs ......... GPT, Claude, Gemini, Llama, Mistral',
    '  Agentic Systems ...... CrewAI, LangChain/LangGraph, AutoGen',
    '  Orchestration ........ n8n, Zapier, Power Automate',
    '  RAG Pipelines ........ Pinecone, Weaviate, pgvector, Chroma',
    '  Cloud AI ............. Azure OpenAI, AWS Bedrock, Vertex AI',
    '  M365 Copilot ......... Studio, Connectors, Plugins',
    '  Data Engineering ..... Python, SQL, Spark, dbt, Airflow',
    '  MLOps ................ MLflow, W&B, Docker, K8s',
    '',
    'PROPRIETARY FRAMEWORKS',
    '----------------------',
    '  HAC ........ Human-Agent Coordination protocol',
    '  ORCHID ..... Outcome-Routed Cognitive Hybrid Intelligence',
    '  SynSense ... Synthetic-Sensory feedback for embodied agents',
    '',
    "  ('hac', 'orchid', 'synsense' for details)",
];
const HISTORY_LINES = [
    '',
    '2024 - now    Founder, Clinic of AI (Lisbon / Zurich)',
    '              Enterprise AI agency. Agentic automations,',
    '              RAG, custom GPTs, Power Platform delivery.',
    '',
    '2022 - 2024   Head of Data & AI, Crayon AG (Switzerland)',
    '              Microsoft top-tier vendor. Led 10+ enterprise',
    '              transformation accounts on Azure OpenAI,',
    '              Copilot, Fabric, Synapse and Power BI.',
    '',
    '              Built and shipped:',
    '                . Internal RAG copilot for a global insurer',
    '                . Document-intelligence pipeline (Top-3 PT bank)',
    '                . Sales-ops agent stack (Swiss machinery group)',
    '',
    '< 2022        Data leadership, BI consulting, ML delivery',
    '              across pharma, finance, manufacturing.',
    '',
    '              Frequent speaker. Strong network across the',
    '              Swiss enterprise & startup scene.',
];
const CONTACT = [
    '',
    '  ████████  C O N T A C T  ████████',
    '',
    '  Email      philippe.kung@clinicofai.com',
    '  Phone      +41 (0)79 960 84 77',
    '  Linktree   linktr.ee/PhilippeKung',
    '  Web        clinicofai.com',
    '',
    '  Open to: enterprise AI engagements,',
    '          fractional CTO/CAIO roles,',
    '          speaking, advisory, M&A.',
];
const PROJECTS = [
    '',
    'PROJECTS / VENTURES',
    '-------------------',
    '  Clinic of AI      Enterprise AI agency.',
    '  Neurolymia        Gamified MMORPG that upskills',
    '                    humans for the AI workplace.',
    '                    (type \'neurolymia\' for the brief)',
    '  HAC framework     Human-Agent Coordination spec.',
    '  ORCHID            Outcome-routed agent topology.',
    '  SynSense          Synthetic sensory feedback layer.',
];
const NEUROLYMIA = [
    '',
    '+----------------------------------------------+',
    '|                 N E U R O L Y M I A          |',
    '|         A gamified MMORPG for the AI age     |',
    '+----------------------------------------------+',
    '',
    '  Premise: most "AI training" is a slide deck',
    '  and a sad LMS. Neurolymia trains people the',
    '  way games train players — quests, guilds,',
    '  loot, real outcomes inside their real tools.',
    '',
    '  Players run agentic missions in the wild,',
    '  level up across HAC competencies, and earn',
    '  receipts that translate to org-level KPIs.',
    '',
    "  Status: pre-seed concept, looking for design",
    "  partners. (Want to talk?  type 'contact'.)",
];
const HAC = [
    '',
    'HAC — Human-Agent Coordination',
    '------------------------------',
    'A protocol for letting humans + LLM agents',
    "co-own a workflow without either side dropping",
    'the ball. Five primitives:',
    '',
    '  1. INTENT   the human writes the why',
    '  2. SCOPE    the agent proposes the boundary',
    '  3. ACT      either side executes a step',
    '  4. RECEIPT  every step leaves a verifiable trace',
    '  5. ESCALATE explicit, named hand-back rules',
    '',
    'It is boring on purpose. Boring scales.',
];
const ORCHID = [
    '',
    'ORCHID — Outcome-Routed Cognitive Hybrid Intelligence',
    '-----------------------------------------------------',
    'Topology for picking the right brain for the job.',
    'A router scores each task across three axes —',
    'risk, irreversibility, value — and dispatches it',
    'to the cheapest brain that still meets the bar:',
    '',
    '  rule -> small LM -> big LM -> human',
    '',
    'Same product, 10x cheaper, and you can show your',
    'compliance team the routing receipts.',
];
const SYNSENSE = [
    '',
    'SynSense — Synthetic Sensory feedback',
    '-------------------------------------',
    'Most agents are blind. SynSense gives them a',
    'cheap, structured sense-channel: screen, voice,',
    'click-stream, doc, calendar, biometric — fused',
    'into a single token-efficient state vector the',
    'LLM can actually reason over.',
];
const HELP = [
    '',
    'AVAILABLE COMMANDS',
    '------------------',
    '  about        who is Philippe',
    '  skills       tech stack & frameworks',
    '  history      career timeline',
    '  projects     ventures & frameworks',
    '  contact      reach out',
    '  hac          Human-Agent Coordination spec',
    '  orchid       cognitive routing topology',
    '  synsense     synthetic sensory layer',
    '  neurolymia   the gamified-upskilling concept',
    '',
    '  whoami       current session identity',
    '  date         show date/time',
    '  ver          terminal + BIOS version',
    '  tree         filesystem tree',
    '  dir          directory listing',
    '  cls          clear the screen',
    '  echo TEXT    print TEXT',
    '  mute         toggle terminal sounds',
    '',
    '  matrix       ...follow the white rabbit',
    '  coffee       order coffee from /dev/cafe',
    '  sudo CMD     try it. seriously.',
    '  cowsay TEXT  ascii cow says TEXT',
    '  exit         power down',
    '',
    "  (Use Up/Down arrows for command history.)",
];
const TREE = [
    '',
    'C:\\',
    ' +--ABOUT.TXT',
    ' +--SKILLS.TXT',
    ' +--HISTORY.LOG',
    ' +--CONTACT.TXT',
    ' +--PROJECTS\\',
    ' |   +--CLINIC-OF-AI\\',
    ' |   +--NEUROLYMIA.exe',
    ' |   +--HAC.dll',
    ' |   +--ORCHID.dll',
    ' |   +--SYNSENSE.sys',
    ' +--SECRETS\\',
    '     +--matrix.bat',
    '     +--coffee.bat',
    '     +--sudo.bat',
];
const DIR_LISTING = [
    '',
    ' Volume in drive C is DATAKULT',
    ' Volume Serial Number is 0867-5309',
    ' Directory of C:\\',
    '',
    ' ABOUT       TXT      4,096   02-21-1984   09:09a',
    ' SKILLS      TXT      8,192   03-01-1991   07:30a',
    ' HISTORY     LOG     16,384   06-25-2024   08:09a',
    ' CONTACT     TXT      2,048   01-23-2025   06:06p',
    ' PROJECTS    <DIR>            05-07-2026   13:37',
    ' SECRETS     <DIR>             ??-??-????   ??:??',
    '         4 File(s)     30,720 bytes',
    '         2 Dir(s)   1,073,741,824 bytes free',
];
const COWSAY = (msg: string): string[] => {
    const m = msg || 'moo';
    const top = ' ' + '_'.repeat(m.length + 2);
    const bot = ' ' + '-'.repeat(m.length + 2);
    return [
        '',
        top,
        `< ${m} >`,
        bot,
        '        \\   ^__^',
        '         \\  (oo)\\_______',
        '            (__)\\       )\\/\\',
        '                ||----w |',
        '                ||     ||',
    ];
};
// ---------- Boot sequence ----------

async function boot(): Promise<void> {
    isTyping = true;
    cursorElement?.classList.add('hidden');
    terminalOutput.innerHTML = '';

    // Brief power-on flash + memory check — sparse clicks so it sounds
    // like a real teletype, not a buzz.
    for (const line of POST) {
        await type(line, { speed: 6, chance: 0.18 });
        await sleep(60);
    }
    await sleep(250);
    playBeep(880, 0.08); // POST beep
    await sleep(220);

    append('\n');
    for (const line of BANNER) await type(line, { speed: 4, chance: 0.08 });
    append('\n');

    await type('Starting MS-DOS...', { speed: 12, chance: 0.5 });
    await sleep(180);
    await type('HIMEM is testing extended memory ... done.', { speed: 8, chance: 0.25 });
    await sleep(120);
    append('\n');
    await type('Welcome to the Datakult Terminal.', { speed: 18, chance: 0.6 });
    await type("Type 'help' for a list of commands.", { speed: 18, chance: 0.6 });
    append('\n');

    isTyping = false;
    showPrompt();
}
// ---------- Command handling ----------
type CommandResult = void | Promise<void>;
type Command = (args: string[], raw: string) => CommandResult;
const commands: Record<string, Command> = {
    help: async () => instant(HELP),
    '?': async () => instant(HELP),
    about: async () => typeLines(ABOUT, { speed: 14 }),
    skills: async () => typeLines(SKILLS, { speed: 8 }),
    history: async () => typeLines(HISTORY_LINES, { speed: 8 }),
    projects: async () => typeLines(PROJECTS, { speed: 10 }),
    contact: async () => typeLines(CONTACT, { speed: 12 }),
    hac: async () => typeLines(HAC, { speed: 10 }),
    orchid: async () => typeLines(ORCHID, { speed: 10 }),
    synsense: async () => typeLines(SYNSENSE, { speed: 10 }),
    neurolymia: async () => {
        typeLines(NEUROLYMIA, { speed: 10 });
        playBeep(440, 0.05);
    },

    whoami: () => instant(['', 'guest@datakult — visitor session', '(Philippe is the host. You are most welcome.)']),
    date: () => {
        const d = new Date();
        instant(['', d.toString()]);
    },
    ver: () => instant(['', 'DATAKULT TERMINAL v4.7 — DOS-mode shell', 'BIOS v4.7  (C) 1984 Datakult Industries']),
    tree: () => instant(TREE),
    dir: () => instant(DIR_LISTING),
    ls: () => instant(DIR_LISTING),
    cls: () => {
        terminalOutput.innerHTML = '';
    },
    clear: () => {
        terminalOutput.innerHTML = '';
    },
    echo: (args) => instant(['', args.join(' ')]),
    mute: () => {
        // Master mute — toggle BOTH SFX and ambient layers together.
        // The top-right panel offers per-layer toggles for finer control.
        const next = !(sfxMuted && ambientMuted);
        setSfxMuted(next);
        setAmbientMuted(next);
        instant(['', next ? '[ sound: OFF ]' : '[ sound: ON ]']);
    },

    exit: async () => {
        instant(['', 'Powering down...', '']);
        await sleep(400);
        powerOff();
    },
    quit: async () => commands.exit([], 'quit'),
    shutdown: async () => commands.exit([], 'shutdown'),

    cowsay: (args) => instant(COWSAY(args.join(' '))),
    coffee: () => instant([
        '',
        "        ( ( (",
        '         ) ) )',
        '      ........',
        '      |      |]   espresso brewing...',
        '      \\      /',
        "       `----'",
        '',
        'Order accepted. Pairs well with REST APIs.',
    ]),

    sudo: async (args) => {
        if (args[0] === 'rm' && args.includes('-rf') && args.includes('/')) {
            await type('Nice try.', { speed: 30 });
            return;
        }
        if (args[0] === 'make' && args[1] === 'me' && args[2] === 'a' && args[3] === 'sandwich') {
            instant(['', 'okay.', '       /    \\', '      | BLT  |', '       \\____/']);
            return;
        }
        instant([
            '',
            `[sudo] password for guest: ${'*'.repeat(8)}`,
            'guest is not in the sudoers file. This incident will be logged.',
            '(...just kidding. nobody logs anything here.)',
        ]);
    },

    matrix: async () => {
        if (matrixActive) {
            instant(['', 'Already in the Matrix. Press ESC to leave.']);
            return;
        }
        instant(['', 'Wake up, Neo...', '']);
        await sleep(400);
        startMatrix();
    },

    // Fun fallthroughs
    ping: (args) => instant(['', `PING ${args[0] || 'datakult.io'} ... 56 bytes from neural.cortex: time=42ms ttl=64`]),
    reboot: async () => {
        instant(['', 'Rebooting...']);
        await sleep(400);
        terminalOutput.innerHTML = '';
        boot();
    },
};
// Aliases
commands.man = commands.help;
commands.bio = commands.about;
commands.cv = commands.history;
commands.resume = commands.history;

async function runCommand(line: string): Promise<void> {
    const trimmed = line.trim();
    if (!trimmed) {
        showPrompt();
        return;
    }
    history.unshift(trimmed);
    if (history.length > 100) history.pop();
    historyIdx = -1;

    const [cmdRaw, ...args] = trimmed.split(/\s+/);
    const cmd = cmdRaw.toLowerCase();

    isTyping = true;
    acceptingInput = false;
    cursorElement?.classList.add('hidden');

    const handler = commands[cmd];
    if (handler) {
        await handler(args, trimmed);
    } else {
        await sleep(80);
        instant(['', `Bad command or file name: '${cmdRaw}'`, "Type 'help' for available commands."]);
    }

    isTyping = false;
    if (!matrixActive) {
        append('\n');
        showPrompt();
    }
}
// ---------- Input line editing ----------
function rerenderCurrentInput(): void {
    // Directly update the text content of our dedicated input span
    currentInputElement.textContent = currentInput;
    terminalOutput.scrollTop = terminalOutput.scrollHeight;
}
document.addEventListener('keydown', async (e: KeyboardEvent) => {
    // Credits modal: Esc closes it from anywhere (independent of terminal state).
    if (e.key === 'Escape' && creditsModal && !creditsModal.classList.contains('hidden')) {
        closeCredits();
        return;
    }

    if (!isScreenOn) return;

    // Mobile text is synchronized by the input event below. Enter and other
    // control keys continue through the normal terminal handler.
    if (e.target === mobileCommandInput && (e.key.length === 1 || e.key === 'Backspace')) return;

    // Skip browser auto-repeat — prevents key SFX from stacking on key-hold.
    if (e.repeat) return;

    if (matrixActive && e.key === 'Escape') {
        stopMatrix();
        return;
    }

    if (isTyping || !acceptingInput) {
        if (e.key === 'Escape' && !matrixActive) return;
        return;
    }

    if (e.key === 'Enter') {
        e.preventDefault();
        playKeyClick('heavy');
        acceptingInput = false;
        cursorElement?.classList.add('hidden');
        append('\n');
        const line = currentInput;
        currentInput = '';
        mobileCommandInput.value = '';
        await runCommand(line);
        return;
    }

    if (e.key === 'Backspace') {
        e.preventDefault();
        if (currentInput.length > 0) {
            currentInput = currentInput.slice(0, -1);
            rerenderCurrentInput();
            playKeyClick('heavy');
        }
        return;
    }

    if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (history.length === 0) return;
        historyIdx = Math.min(historyIdx + 1, history.length - 1);
        currentInput = history[historyIdx];
        rerenderCurrentInput();
        return;
    }

    if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (historyIdx <= 0) {
            historyIdx = -1;
            currentInput = '';
        } else {
            historyIdx--;
            currentInput = history[historyIdx];
        }
        rerenderCurrentInput();
        return;
    }

    if (e.key === 'Tab') {
        e.preventDefault();
        const partial = currentInput.toLowerCase();
        const matches = Object.keys(commands).filter(c => c.startsWith(partial));
        if (matches.length === 1) {
            currentInput = matches[0];
            rerenderCurrentInput();
        } else if (matches.length > 1 && partial.length > 0) {
            append('\n' + matches.join('  ') + '\n');
            append(PROMPT + currentInput);
        }
        return;
    }

    if (e.key === 'Escape') {
        playKeyClick('heavy');
        if (currentInput) {
            currentInput = '';
            rerenderCurrentInput();
        }
        return;
    }

    // Printable characters
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        currentInput += e.key;
        rerenderCurrentInput();
        playKeyClick('normal');
    }
});

mobileCommandInput.addEventListener('input', () => {
    if (!isScreenOn || isTyping || !acceptingInput) return;
    const previousLength = currentInput.length;
    currentInput = mobileCommandInput.value;
    rerenderCurrentInput();
    if (currentInput.length !== previousLength) {
        playKeyClick(currentInput.length < previousLength ? 'heavy' : 'normal');
    }
});
// ---------- Power ----------
function powerOff(): void {
    isScreenOn = false;
    isTyping = false;
    acceptingInput = false;
    document.body.classList.remove('crt-active');
    document.body.classList.add('crt-shutdown');
    setTimeout(() => {
        document.body.classList.remove('crt-shutdown');
        terminalContainer?.classList.add('hidden');
        terminalOutput.innerHTML = '';
    }, 500);
    humSound?.pause();
    ambientSound?.pause();
    ambientStarted = false; // re-arm so power-on restarts the layers cleanly
    if (matrixActive) stopMatrix();
}

async function handlePowerButtonClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (isScreenOn) {
        powerOff();
        return;
    }

    isScreenOn = true;
    ensureAmbientStarted();
    document.body.classList.add('crt-active');
    terminalContainer?.classList.remove('hidden');
    await boot();
}
// ---------- Matrix rain ----------
let matrixRAF = 0;
function startMatrix(): void {
    if (!matrixCanvas) return;
    matrixActive = true;
    matrixCanvas.classList.remove('hidden');
    const ctx = matrixCanvas.getContext('2d');
    if (!ctx) return;
    const resize = () => {
        matrixCanvas.width = matrixCanvas.clientWidth;
        matrixCanvas.height = matrixCanvas.clientHeight;
    };
    resize();
    const cols = Math.floor(matrixCanvas.width / 14);
    const drops = new Array(cols).fill(0).map(() => Math.random() * matrixCanvas.height);
    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノ0123456789DATAKULT';
    const tick = () => {
        if (!matrixActive) return;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
        ctx.fillRect(0, 0, matrixCanvas.width, matrixCanvas.height);
        ctx.fillStyle = '#33ff33';
        ctx.font = '14px VT323, monospace';
        for (let i = 0; i < drops.length; i++) {
            const ch = chars[Math.floor(Math.random() * chars.length)];
            ctx.fillText(ch, i * 14, drops[i]);
            if (drops[i] > matrixCanvas.height && Math.random() > 0.975) drops[i] = 0;
            drops[i] += 14;
        }
        matrixRAF = requestAnimationFrame(tick);
    };
    tick();
}
function stopMatrix(): void {
    matrixActive = false;
    cancelAnimationFrame(matrixRAF);
    matrixCanvas?.classList.add('hidden');
    // Restore prompt
    append('\n');
    append('Connection terminated. Welcome back.\n');
    showPrompt();
}
// ---------- Wire up ----------
changePageButton?.addEventListener('click', handlePowerButtonClick);
escapeButton?.addEventListener('click', () => {
    if (matrixActive) stopMatrix();
    else if (isScreenOn) powerOff();
});
monitorBezel?.addEventListener('click', (event) => {
    if (!isScreenOn && event.target === monitorBezel) handlePowerButtonClick(event);
    else if (isScreenOn && window.matchMedia('(pointer: coarse)').matches) {
        mobileCommandInput.value = currentInput;
        mobileCommandInput.focus({ preventScroll: true });
    }
});
// Click to focus / resume audio
document.addEventListener('click', () => {
    if (audioCtx.state === 'suspended') audioCtx.resume();
});
